import { formatLabel } from '@/lib/utils/formatting'
import { TrackBar } from '../TrackBar'
import { DECK_CHART_PRIMARY_COLOR } from '../../../foundations/chart-colors'

/**
 * DistributionBars — a single-series share-of-base list, built on `TrackBar`.
 *
 * The compact KPI-column read for a `DistributionPayload` (D3's
 * `kpi.secondary`, or the plot slot on its own): one bar per option, share
 * leads, bar follows.
 *
 * Two unit props, deliberately separate. `unit` is a SHORT SUFFIX SYMBOL that
 * belongs glued to the figure ("%", "/5", a currency sign); anything else is
 * prose and is dropped from the figure, because "62" + "1-5 scale" printed
 * together reads as "621-5 scale". `unitLabel` is that prose, printed ONCE
 * above the bars instead of on every row.
 *
 * Labels are passed in, never hardcoded English or an i18n import: deck-ui has
 * no translation runtime of its own (see `PriceCurveChartLabels` for the same
 * pattern), so a caller in the app supplies translated strings.
 *
 * No hover tooltip, deliberately: the share and the option label are printed on
 * the row above each bar, so a tooltip would only repeat them.
 */
export interface DistributionBarsRow {
  label: string
  share: number
}

export interface DistributionBarsLabels {
  empty: string
}

export const DEFAULT_DISTRIBUTION_BARS_LABELS: DistributionBarsLabels = {
  empty: 'No distribution data available',
}

export interface DistributionBarsProps {
  rows: Array<DistributionBarsRow>
  /** Short suffix symbol only: "%", "/5", "$". Prose is ignored here. */
  unit?: string
  /** Prose unit ("1-5 scale", "Mean out of 5"), printed once above the bars. */
  unitLabel?: string
  labels?: Partial<DistributionBarsLabels>
}

/**
 * Currency signs short enough to sit against the figure. Spelled out rather
 * than matched with `\p{Sc}`, which needs the `u` flag and an ES6 target.
 */
const CURRENCY_SIGNS = '$\u20ac\u00a3\u00a5\u20b9\u20bd\u20a9\u20aa\u20ab\u20b4\u20a6\u20b1\u0e3f'

/**
 * A suffix the figure can carry: a percent sign, a "/5"-style scale
 * denominator, or a single currency symbol. Everything else ("Share",
 * "1-5 scale", "Mean out of 5") is prose and belongs in `unitLabel`.
 */
const SUFFIX_SYMBOL = new RegExp(`^(%|\\/\\s?\\d+(\\.\\d+)?|[${CURRENCY_SIGNS}])$`)

export function isSuffixSymbolUnit(unit: string | undefined): boolean {
  if (!unit) return false
  return SUFFIX_SYMBOL.test(unit.trim())
}

function formatShare(share: number, unit?: string): string {
  const rounded = Math.round(share * 10) / 10
  if (unit === undefined) return `${rounded}%`
  if (isSuffixSymbolUnit(unit)) return `${rounded}${unit}`
  return String(rounded)
}

export function DistributionBars(props: DistributionBarsProps) {
  const { rows, unit, unitLabel, labels: labelsProp } = props
  const labels: DistributionBarsLabels = { ...DEFAULT_DISTRIBUTION_BARS_LABELS, ...labelsProp }

  if (rows.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-muted-foreground min-h-48"
        data-testid="distribution-bars"
      >
        {labels.empty}
      </div>
    )
  }

  return (
    <div className="w-full space-y-5" data-testid="distribution-bars">
      {unitLabel && (
        <span
          data-testid="distribution-bars-unit-label"
          className="block text-[10px] text-muted-foreground"
        >
          {unitLabel}
        </span>
      )}
      {rows.map((row, index) => (
        <div
          key={`${row.label}-${index}`}
          className="space-y-1.5"
          data-testid={`distribution-bars-row-${index}`}
        >
          <div className="flex items-center justify-between gap-4">
            <span className="text-[14px] font-normal text-foreground">
              {formatLabel(row.label)}
            </span>
            <span className="text-sm font-semibold tabular-nums shrink-0">
              {formatShare(row.share, unit)}
            </span>
          </div>
          {/*
            No tooltip on this family. Every bar already prints its label and
            its share on the row directly above it, so the hover card repeated
            what the reader could see while adding a keyboard stop with nothing
            new in it. `PlotTooltip` itself is untouched -- the bar families
            whose values are NOT printed beside them still carry it.
          */}
          <TrackBar
            widthPct={row.share}
            color={DECK_CHART_PRIMARY_COLOR}
            value={formatShare(row.share, unit)}
            label={formatLabel(row.label)}
            withTooltip={false}
          />
        </div>
      ))}
    </div>
  )
}
